import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
// import MainLayout from '../../layouts/MainLayout';
import './BigData.css';
import { Bar, Line, Pie, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';

const fallbackData = [
  { dateRead: '01-01-2024', pageCount: 10, timeSpent: '30:00' },
  { dateRead: '02-01-2024', pageCount: 20, timeSpent: '45:00' },
  { dateRead: '03-01-2024', pageCount: 15, timeSpent: '20:00' },
];

// Bar Chart Component
const ReadLogBarChart = ({ data, title }) => {
  // console.log("Inside barchart: ",data,title);

  if (!data || data.length === 0) {
    return <div className='flex flex-row justify-center text-xl text-white'>No data available</div>;
  }

   // Ensure date format is correct and sort in ascending order
   const sortedData = [...data].sort((a, b) => {
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day);
    };
  
    return parseDate(a.dateRead) - parseDate(b.dateRead);
  });

  const chartData = {
    labels: sortedData.map(entry => entry.dateRead),
    datasets: [
      {
        label: 'Time Spent (seconds)',
        data: sortedData.map(entry => {
          const timeParts = entry.timeSpent.split(':').map(Number);
          return timeParts[0] * 60 + timeParts[1];
        }),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'white',
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => tooltipItems[0]?.label ?? '',
          label: (tooltipItem) => `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
    scales: {
      x: {
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
      y: {
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
  };

  return <Bar data={chartData} options={options} />;
};


// Transform the data to count records per date
const transformGraphDataForLineChart = (graphData) => {
  const result = [];
  const dateCounts = {};

  // Count records per date
  Object.keys(graphData).forEach(date => {
    dateCounts[date] = graphData[date].length;
  });

  // Convert dateCounts object to an array of objects with date and count
  Object.keys(dateCounts).forEach(date => {
    result.push({
      dateRead: date,
      count: dateCounts[date],
    });
  });

  // Sort by date
  result.sort((a, b) => new Date(a.dateRead) - new Date(b.dateRead));

  return result;
};

const ReadLogLineChart = ({ data }) => {

    // Ensure date format is correct and sort in ascending order
    const sortedData = [...data].sort((a, b) => {
      const parseDate = (dateStr) => {
        const [day, month, year] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      };
    
      return parseDate(a.dateRead) - parseDate(b.dateRead);
    });

  // Prepare data for the chart
  const chartData = {
    labels: sortedData.map(entry => entry.dateRead),
    datasets: [
      {
        label: 'Records Count',
        data: sortedData.map(entry => entry.count),
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        fill: false,
      },
    ],
  };

  // Determine the min and max counts for y-axis interval
  const counts = data.map(entry => entry.count);
  const minCount = Math.min(...counts);
  const maxCount = Math.max(...counts);
  const yAxisStepSize = Math.ceil((maxCount - minCount) / 20);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'white',
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => tooltipItems[0]?.label ?? '',
          label: (tooltipItem) => `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
    scales: {
      x: {
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
      y: {
        ticks: {
          color: 'white',
          stepSize: yAxisStepSize, // Set y-axis interval
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
  };

  return <Line data={chartData} options={options} />;
};



// Pie Chart Component
const ReadLogPieChart = ({ data }) => {
    // Ensure date format is correct and sort in ascending order
    const sortedData = [...data].sort((a, b) => {
      const parseDate = (dateStr) => {
        const [day, month, year] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      };
    
      return parseDate(a.dateRead) - parseDate(b.dateRead);
    });

  const chartData = {
    labels: sortedDdata.map(entry => entry.dateRead),
    datasets: [
      {
        label: 'Time Spent',
        data: sortedData.map(entry => parseInt(entry.timeSpent.split(':').reduce((acc, time) => (60 * acc) + +time))),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'white',
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => tooltipItems[0]?.label ?? '',
          label: (tooltipItem) => `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
  };

  return <Pie data={chartData} options={options} />;
};


const groupByTimeSlotAndInterval = (graphData) => {
  const result = [];
  const timeSlots = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]; // 2-hour time slots
  const hoursInDay = Array.from({ length: 24 }, (_, i) => i); // Hours from 0 to 23

  // Initialize result array with all time slots and hours
  timeSlots.forEach(timeSlot => {
    hoursInDay.forEach(hour => {
      result.push({
        x: timeSlot,
        y: hour,
        count: 0,
      });
    });
  });

  // Populate result array with actual data
  Object.keys(graphData).forEach(date => {
    const entriesForDate = graphData[date];

    if (Array.isArray(entriesForDate)) {
      entriesForDate.forEach(entry => {
        const dateObject = new Date(entry.dateRead);
        const hours = dateObject.getUTCHours(); // Extract hour from date
        const timeSlot = Math.floor(hours / 2) * 2; // Determine the 2-hour time slot (0, 2, 4, ..., 22)

        // Convert read time from milliseconds to minutes
        const readTimeInMinutes = entry.readTimeInMiliseconds / 60000;

        // Find the matching slot and increment count
        const existing = result.find(item => item.x === timeSlot && item.y === hours);

        if (existing) {
          existing.count += 1;
        }
      });
    }
  });

  return result;
};

const ReadLogScatterPlot = ({ data }) => {
  const chartData = {
    datasets: [
      {
        label: 'Reading Records',
        data: data.map(entry => ({
          x: entry.x, // Time Slot (0, 2, 4, ..., 22)
          y: entry.y, // Hour of the day (0 to 23)
          r: Math.min(entry.count * 2, 10) // Circle size, scaled by count
        })),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
        pointRadius: (context) => context.raw.r, // Circle radius based on count
      },
    ],
  };

  const options = {
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time Slots (Hours)',
          color: 'white',
        },
        ticks: {
          color: 'white',
          stepSize: 2,  
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Hour of the Day',
          color: 'white',
        },
        ticks: {
          color: 'white',
          stepSize: 1,  
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.2)',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: 'white',
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => 
            `Time Slot: ${tooltipItem.raw.x}, Hour: ${tooltipItem.raw.y}, Count: ${tooltipItem.raw.r / 2}`,
        },
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  return <Scatter data={chartData} options={options} />;
};


// Helper function to convert milliseconds to 'minutes:seconds'
const millisecondsToTime = (milliseconds) => {
  const minutes = Math.floor(milliseconds / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Helper function to format date to 'dd-mm-yyyy'
const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return `${day}-${month}-${year}`;
};

// Helper function to aggregate data
const aggregateData = (data) => {
  const aggregatedData = Object.entries(data).reduce((acc, [date, entries]) => {
    if (!acc[date]) {
      acc[date] = { pageCount: 0, timeSpent: 0 };
    }

    entries.forEach(entry => {
      acc[date].pageCount += entry.pageNumber || 0;
      acc[date].timeSpent += entry.readTimeInMiliseconds || 0;
    });

    return acc;
  }, {});

  // Convert aggregated data to array format and sort by date descending
  return Object.entries(aggregatedData)
    .map(([date, { pageCount, timeSpent }]) => ({
      dateRead: formatDate(date),
      pageCount,
      timeSpent: millisecondsToTime(timeSpent)
    }))
    .sort((a, b) => {
      const [dayA, monthA, yearA] = a.dateRead.split('-').map(Number);
      const [dayB, monthB, yearB] = b.dateRead.split('-').map(Number);
      return new Date(yearB, monthB - 1, dayB) - new Date(yearA, monthA - 1, dayA);
    });
};


// Main Component
function BookReadPageReadCharts() {
  const [selectedChart, setSelectedChart] = useState('Bar');
  const location = useLocation();
  const [data, setData] = useState([]);
  const [barPieChartData, setBarPieChartData] = useState([]);
  const [lineChartData, setLineChartData] = useState([]);
  const [scatterPlotChartData, setScatterPlotChartData] = useState([]);

  useEffect(() => {
    // console.log("Received Location State: ", location.state);
    if (location.state && location.state.graphData && typeof location.state.graphData === 'object') {
      const formattedData = aggregateData(location.state.graphData); 
      const transformedDataForLineChart = transformGraphDataForLineChart(location.state.graphData); 
      // const transformedDataForScatterPlotChart = transformGraphDataForScatterPlotChart(location.state.graphData); 
      const groupedData = groupByTimeSlotAndInterval(location.state.graphData); 

      // console.log("Formatted Data for Bar & Pie Chart: ", formattedData); 
      // console.log("Transformed Data for Line Chart: ", transformedDataForLineChart); 
      // console.log("Transformed Data for Scatter Plot: ", groupedData); 

      setBarPieChartData(formattedData); 
      setLineChartData(transformedDataForLineChart); 
      // setScatterPlotChartData(transformedDataForScatterPlotChart); 
      setScatterPlotChartData(groupedData);

    } else {
      setData(fallbackData);
    }
  }, [location.state]);

  const handleChartChange = useCallback((chartType) => {
    setSelectedChart(chartType);
  }, []);

  const renderChart = () => {
    switch (selectedChart) {
      case 'Bar':
        return <ReadLogBarChart data={barPieChartData} title="Read Time Bar Chart" />;
      case 'Line':
        return <ReadLogLineChart data={lineChartData} title="Read Time Line Chart" />;
      case 'Pie':
        return <ReadLogPieChart data={barPieChartData} title="Read Time Pie Chart" />;
      case 'Scatter':
        // console.log("scatterPlotChartData: ",scatterPlotChartData);
        return <ReadLogScatterPlot data={scatterPlotChartData} title="Read Time Scatter Plot Chart" />;
      default:
        return <div className='text-yellow-200'>Select a chart to display</div>;
    }
  };

  return (
    <div>
      <div className='flex flex-row justify-center mb-4 mt-12'>
        <button onClick={() => handleChartChange('Bar')} className="chart-button">Bar Graph</button>
        <button onClick={() => handleChartChange('Line')} className="ml-4 chart-button">Line Chart</button>
        <button onClick={() => handleChartChange('Pie')} className="ml-4 chart-button">Pie Chart</button>
        <button onClick={() => handleChartChange('Scatter')} className="ml-4 chart-button">Scatter Chart</button>
      </div>
      <div className="chart-container min-h-[500px]">
        {renderChart()}
      </div>
      <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>            
    </div>
  );
}

export default BookReadPageReadCharts;
